/**
 * #1316 review round 2 — a CUT is spent once its paste ran. The paste FINISHES as the identity that started it, even
 * after a tenant / identity switch, so the moved items' `parentId`s on the clipboard are stale whatever the session is
 * now; re-pasting them would move them a second time (a "replace" on a just-moved file deletes it). `papan-clipboard`
 * lives in localStorage across sessions, so the spent cut is cleared ALWAYS — never gated on the session like the
 * paste's toasts — but only while the clipboard still holds THIS cut: the next session may have cut or copied
 * something else meanwhile, which stays.
 */

const CLIPBOARD_KEY = 'papan-clipboard'

export interface CutClipboard {
  items: ReadonlyArray<{ id: string }>
  isCut: boolean
  tenantId?: string
}

function sameCut(stored: unknown, cut: CutClipboard): boolean {
  if (!stored || typeof stored !== 'object') return false
  const s = stored as Partial<CutClipboard>
  if (s.isCut !== true || !Array.isArray(s.items)) return false
  if ((s.tenantId ?? null) !== (cut.tenantId ?? null)) return false
  if (s.items.length !== cut.items.length) return false
  return s.items.every((it, i) => !!it && (it as { id?: unknown }).id === cut.items[i].id)
}

/** Clears `papan-clipboard` iff it still holds `cut` (a cut, same tenant, same item ids in order). Returns whether it
 *  cleared. A copy is never spent. */
export function clearSpentCut(cut: CutClipboard): boolean {
  if (!cut.isCut) return false
  try {
    const raw = localStorage.getItem(CLIPBOARD_KEY)
    if (!raw || !sameCut(JSON.parse(raw), cut)) return false
    localStorage.removeItem(CLIPBOARD_KEY)
    return true
  } catch {
    return false
  }
}
